import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { useQuery, useQueryClient } from 'react-query';
import { adminTeamsAPI, adminLeagueManagementAPI } from '../services/adminApi';
import { getCantonName, getCantonEmoji } from '../utils/cantons';
import AdminTeamMemberProfileCard, { AdminTeamMembersGrid } from './AdminTeamMemberProfileCard';

const DivisionDetailContainer = styled.div`
  background: white;
  border-radius: 12px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  overflow: hidden;
`;

const Header = styled.div`
  padding: 2rem;
  border-bottom: 1px solid #e5e7eb;
  background: linear-gradient(135deg, #043663 0%, #043663 100%);
  color: white;
  
  @media (max-width: 768px) {
    padding: 1.5rem 1rem;
  }
`;

const BackButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;
  margin-bottom: 1rem;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }
  
  @media (max-width: 768px) {
    padding: 0.4rem 0.75rem;
    font-size: 0.8rem;
    margin-bottom: 0.75rem;
  }
`;

const Title = styled.h2`
  margin: 0 0 0.5rem 0;
  font-size: 1.5rem;
  font-weight: 700;
  
  @media (max-width: 768px) {
    font-size: 1.25rem;
  }
`;

const Subtitle = styled.p`
  margin: 0;
  opacity: 0.9;
  font-size: 1rem;
  
  @media (max-width: 768px) {
    font-size: 0.9rem;
  }
`;

const Content = styled.div`
  padding: 2rem;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 2rem;
  
  @media (max-width: 768px) {
    grid-template-columns: repeat(auto-fit, minmax(150px, 1fr));
    gap: 0.75rem;
    margin-bottom: 1.5rem;
  }
`;

const StatCard = styled.div`
  background: linear-gradient(135deg, #f8fafc 0%, #e2e8f0 100%);
  border-radius: 12px;
  padding: 1.5rem;
  text-align: center;
  border: 1px solid #e5e7eb;
  
  @media (max-width: 768px) {
    padding: 1rem;
  }
`;

const StatNumber = styled.div`
  font-size: 2rem;
  font-weight: 700;
  color: #043663;
  margin-bottom: 0.5rem;
  
  @media (max-width: 768px) {
    font-size: 1.5rem;
  }
`;

const StatLabel = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const SectionTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
  
  @media (max-width: 768px) {
    font-size: 1.1rem;
    margin-bottom: 0.75rem;
  }
`;

const TableWrapper = styled.div`
  width: 100%;
  overflow-x: auto;
  -webkit-overflow-scrolling: touch;
  border-radius: 8px;
  box-shadow: 0 1px 3px 0 rgba(0, 0, 0, 0.1);
  
  @media (max-width: 768px) {
    margin: 0 -1rem;
    padding: 0 1rem;
  }
  
  &::-webkit-scrollbar {
    height: 8px;
  }
  
  &::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb {
    background: #888;
    border-radius: 10px;
  }
  
  &::-webkit-scrollbar-thumb:hover {
    background: #555;
  }
`;

const TeamsTable = styled.table`
  width: 100%;
  border-collapse: collapse;
  background: white;
  border-radius: 8px;
  min-width: 600px;
  
  @media (max-width: 768px) {
    font-size: 0.8rem;
  }
`;

const TableHeader = styled.thead`
  background: #f8fafc;
  
  @media (max-width: 768px) {
    display: table-header-group;
  }
`;

const HeaderRow = styled.tr`
  border-bottom: 2px solid #e5e7eb;
`;

const HeaderCell = styled.th`
  padding: 1rem;
  text-align: left;
  font-weight: 600;
  color: #374151;
  font-size: 0.875rem;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  white-space: nowrap;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.75rem;
  }
`;

const TableBody = styled.tbody``;

const TableRow = styled.tr`
  border-bottom: 1px solid #e5e7eb;

  &:hover {
    background-color: #f8fafc;
  }

  &:last-child {
    border-bottom: none;
  }
`;

const TableCell = styled.td`
  padding: 1rem;
  font-size: 0.875rem;
  color: #374151;
  
  @media (max-width: 768px) {
    padding: 0.75rem 0.5rem;
    font-size: 0.8rem;
  }
`;

const TeamName = styled.div`
  font-weight: 600;
  color: #1f2937;
`;

const TeamCanton = styled.div`
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 0.25rem;
`;

const SkillLevel = styled.span`
  background: #dbeafe;
  color: #043663;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const StatusBadge = styled.span`
  display: inline-block;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;

  &.active {
    background: #d1fae5;
    color: #065f46;
  }

  &.inactive {
    background: #fee2e2;
    color: #991b1b;
  }
`;

const ViewDetailsButton = styled.button`
  background: #043663;
  color: white;
  border: none;
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-size: 0.75rem;
  font-weight: 500;
  cursor: pointer;
  transition: background-color 0.2s ease;

  &:hover {
    background: #032a4f;
  }
`;

// Team Details Modal Styles
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 2000;
  padding: 2rem;
`;

const ModalContent = styled.div`
  background: white;
  border-radius: 16px;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1);
  max-width: 1200px;
  width: 100%;
  max-height: 90vh;
  overflow-y: auto;
  
  @media (max-width: 768px) {
    max-width: 100%;
  }
`;

const ModalHeader = styled.div`
  padding: 2rem 2rem 1rem;
  border-bottom: 1px solid #e5e7eb;
  display: flex;
  justify-content: space-between;
  align-items: center;
`;

const ModalTitle = styled.h2`
  margin: 0;
  font-size: 1.5rem;
  font-weight: 600;
  color: #1f2937;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 1.5rem;
  cursor: pointer;
  color: #6b7280;
  padding: 0.5rem;
  border-radius: 8px;
  
  &:hover {
    background: #f3f4f6;
    color: #374151;
  }
`;

const ModalBody = styled.div`
  padding: 2rem;
`;

const TeamInfoSection = styled.div`
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: #f8fafc;
  border-radius: 12px;
  border: 1px solid #e5e7eb;
`;

const TeamInfoGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
  gap: 1rem;
  margin-bottom: 1rem;
`;

const InfoItem = styled.div`
  .label {
    font-size: 0.75rem;
    font-weight: 600;
    color: #6b7280;
    text-transform: uppercase;
    letter-spacing: 0.05em;
    margin-bottom: 0.25rem;
  }
  
  .value {
    font-size: 0.875rem;
    color: #1f2937;
    font-weight: 500;
  }
`;

const MembersSection = styled.div`
  margin-top: 2rem;
`;

const MembersTitle = styled.h3`
  margin: 0 0 1rem 0;
  font-size: 1.25rem;
  font-weight: 600;
  color: #1f2937;
`;

const MembersGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: 1.5rem;
`;

const MemberCard = styled.div`
  background: white;
  border: 1px solid #e5e7eb;
  border-radius: 12px;
  padding: 1.5rem;
  transition: box-shadow 0.3s ease;
  
  &:hover {
    box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
  }
`;

const MemberHeader = styled.div`
  display: flex;
  align-items: center;
  gap: 1rem;
  margin-bottom: 1rem;
`;

const MemberAvatar = styled.div`
  width: 50px;
  height: 50px;
  border-radius: 50%;
  background: linear-gradient(135deg, #043663 0%, #0a4d7a 100%);
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: 600;
  font-size: 1.125rem;
`;

const MemberBasicInfo = styled.div`
  flex: 1;
`;

const MemberName = styled.div`
  font-size: 1.125rem;
  font-weight: 600;
  color: #1f2937;
  margin-bottom: 0.25rem;
`;

const MemberRole = styled.div`
  font-size: 0.875rem;
  color: #6b7280;
  display: flex;
  align-items: center;
  gap: 0.25rem;
`;

const MemberDetails = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 0.75rem;
  
  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const MemberDetailItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.5rem 0;
  border-bottom: 1px solid #f3f4f6;
  
  &:last-child {
    border-bottom: none;
  }
`;

const MemberDetailLabel = styled.span`
  font-size: 0.875rem;
  color: #6b7280;
  font-weight: 500;
`;

const MemberDetailValue = styled.span`
  font-size: 0.875rem;
  color: #1f2937;
  font-weight: 500;
`;

const SkillLevelBadge = styled.span`
  background: #fef3c7;
  color: #92400e;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const DivisionBadge = styled.span`
  background: #dbeafe;
  color: #043663;
  padding: 0.25rem 0.5rem;
  border-radius: 4px;
  font-size: 0.75rem;
  font-weight: 500;
`;

const LoadingContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const ErrorContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #d52b1e;
`;

const EmptyContainer = styled.div`
  padding: 3rem;
  text-align: center;
  color: #6b7280;
`;

const AdminDivisionDetail = ({ division, seasonId, onBack }) => {
  const [selectedTeam, setSelectedTeam] = useState(null);
  const [showTeamDetails, setShowTeamDetails] = useState(false);
  const queryClient = useQueryClient();

  // CRITICAL: Fetch division data from Division Balancing endpoint to get the TRUE count
  // This ensures we use the same source of truth as Division Balancing tab
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data: divisionsOverviewData } = useQuery(
    ['divisions-overview', seasonId],
    () => adminLeagueManagementAPI.getDivisionsOverview(seasonId),
    {
      enabled: !!seasonId,
      refetchInterval: showTeamDetails ? false : 60000 // Disabled when team details modal is open
    }
  );

  // Find the matching division from Division Balancing data to get actual_team_count
  const divisionFromBalancing = divisionsOverviewData?.data?.divisions?.find(
    d => d.id === division.id
  );
  // Use actual_team_count from Division Balancing if available (the truth), otherwise fall back to division.team_count
  const trueTeamCount = divisionFromBalancing 
    ? parseInt(divisionFromBalancing.actual_team_count || 0)
    : parseInt(division.team_count || 0);

  // Fetch teams for this specific division
  // CRITICAL: Pass season_id to use team_division_assignments (season-aware)
  // If seasonId is not provided, falls back to teams.division_id (backward compatibility)
  // Disable auto-refresh when viewing team details to prevent overwriting
  const { data: teamsData, isLoading: teamsLoading, error: teamsError } = useQuery(
    ['divisionTeams', division.id, seasonId],
    () => adminTeamsAPI.getAll({ 
      division: division.id, 
      season_id: seasonId || undefined, // Only include if provided
      limit: 1000 
    }),
    {
      refetchInterval: showTeamDetails ? false : 60000 // Disabled when team details modal is open
    }
  );

  // Fetch detailed team data when a team is selected
  // Disable auto-refresh to prevent overwriting when viewing details
  const { data: teamDetailsData, isLoading: teamDetailsLoading } = useQuery(
    ['teamDetails', selectedTeam?.id, seasonId],
    () => adminTeamsAPI.getTeamDetails(selectedTeam?.id, { seasonId: seasonId || undefined }),
    {
      enabled: !!selectedTeam,
      refetchInterval: false, // Disabled - manual refresh only to prevent overwriting
      staleTime: 0, // Always consider data stale to force fresh fetch
      cacheTime: 0, // Don't cache to ensure fresh data
      onSuccess: (data) => {
        console.log('📞 Team Details Response:', data);
        console.log('📞 Phone Number:', data?.data?.team?.phone_number);
      }
    }
  );

  const handleViewTeamDetails = (team) => {
    setSelectedTeam(team);
    setShowTeamDetails(true);
    // Force refetch team details when modal opens to get fresh data
    setTimeout(() => {
      queryClient.invalidateQueries(['teamDetails', team.id]);
      queryClient.refetchQueries(['teamDetails', team.id]);
    }, 100);
  };

  const handleBackToTeams = () => {
    setShowTeamDetails(false);
    setSelectedTeam(null);
  };

  // Lock body scroll when team details modal is open
  useEffect(() => {
    if (showTeamDetails) {
      // Store the current scroll position
      const scrollY = window.scrollY;
      // Get the scrollbar width to prevent layout shift
      const scrollbarWidth = window.innerWidth - document.documentElement.clientWidth;
      
      // Lock the body scroll without affecting fixed elements like sidebar
      document.body.style.overflow = 'hidden';
      document.body.style.paddingRight = `${scrollbarWidth}px`;
      
      // Also add padding to the sidebar to prevent it from shifting
      const sidebar = document.querySelector('aside');
      if (sidebar) {
        sidebar.style.paddingRight = `${scrollbarWidth}px`;
      }
      
      return () => {
        // Restore scroll when modal closes
        document.body.style.overflow = '';
        document.body.style.paddingRight = '';
        if (sidebar) {
          sidebar.style.paddingRight = '';
        }
        window.scrollTo(0, scrollY);
      };
    }
  }, [showTeamDetails]);

  if (teamsLoading) {
    return (
      <DivisionDetailContainer>
        <LoadingContainer>Loading division teams...</LoadingContainer>
      </DivisionDetailContainer>
    );
  }

  if (teamsError) {
    return (
      <DivisionDetailContainer>
        <ErrorContainer>Error loading teams: {teamsError.message}</ErrorContainer>
      </DivisionDetailContainer>
    );
  }

  // Filter out disabled teams - use season_is_active if available (season-aware), otherwise use is_active
  const teams = (teamsData?.data?.teams || []).filter(team => {
    // If season_is_active is available (season-aware), use it; otherwise use is_active
    const isTeamActive = team.season_is_active !== undefined ? team.season_is_active : team.is_active;
    return isTeamActive !== false; // Include teams that are active (true or undefined/null)
  });
  
  // CRITICAL: Use trueTeamCount from Division Balancing endpoint (the source of truth)
  // This ensures the count matches exactly what Division Balancing shows
  const totalTeams = trueTeamCount;
  const totalMembers = teams.reduce((sum, team) => sum + parseInt(team.member_count || 0), 0);

  return (
    <DivisionDetailContainer>
      <Header>
        <BackButton onClick={onBack}>← Back</BackButton>
        <Title>{division.name}</Title>
        <Subtitle>
          {getCantonEmoji(division.canton)} {getCantonName(division.canton)} • 
          Skill Level: {division.skill_level_min} - {division.skill_level_max}
        </Subtitle>
      </Header>

      <Content>
        <StatsGrid>
          <StatCard>
            <StatNumber>{totalTeams}</StatNumber>
            <StatLabel>Total Teams</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{totalMembers}</StatNumber>
            <StatLabel>Total Players</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>{division.skill_level_min} - {division.skill_level_max}</StatNumber>
            <StatLabel>Skill Range</StatLabel>
          </StatCard>
          <StatCard>
            <StatNumber>
              <StatusBadge className={division.is_active ? 'active' : 'inactive'}>
                {division.is_active ? 'Active' : 'Inactive'}
              </StatusBadge>
            </StatNumber>
            <StatLabel>Status</StatLabel>
          </StatCard>
        </StatsGrid>

        <SectionTitle>Teams in this Division ({totalTeams})</SectionTitle>

        {teams.length === 0 ? (
          <EmptyContainer>
            No teams assigned to this division yet.
          </EmptyContainer>
        ) : (
          <TableWrapper>
            <TeamsTable>
              <TableHeader>
                <HeaderRow>
                  <HeaderCell style={{ width: '60px' }}>SR No</HeaderCell>
                  <HeaderCell>Team</HeaderCell>
                  <HeaderCell>Skill Level</HeaderCell>
                  <HeaderCell>Members</HeaderCell>
                  <HeaderCell>Status</HeaderCell>
                  <HeaderCell>Actions</HeaderCell>
                </HeaderRow>
              </TableHeader>
              <TableBody>
                {teams.map((team, index) => (
                  <TableRow key={team.id}>
                    <TableCell style={{ textAlign: 'center', fontWeight: '600', color: '#6b7280' }}>
                      {index + 1}
                    </TableCell>
                    <TableCell>
                      <TeamName>{team.team_name}</TeamName>
                      <TeamCanton>{team.canton}</TeamCanton>
                    </TableCell>
                    <TableCell>
                      <SkillLevel>{team.team_skill_level}</SkillLevel>
                    </TableCell>
                    <TableCell>{team.member_count}</TableCell>
                    <TableCell>
                      <StatusBadge className={team.is_active ? 'active' : 'inactive'}>
                        {team.is_active ? 'Active' : 'Inactive'}
                      </StatusBadge>
                    </TableCell>
                    <TableCell>
                      <ViewDetailsButton onClick={() => handleViewTeamDetails(team)}>
                        View Details
                      </ViewDetailsButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </TeamsTable>
          </TableWrapper>
        )}

        {/* Team Details Modal */}
        {showTeamDetails && selectedTeam && (
          <ModalOverlay onClick={handleBackToTeams}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalHeader>
                <ModalTitle>Team Details - {selectedTeam.team_name}</ModalTitle>
                <CloseButton onClick={handleBackToTeams}>×</CloseButton>
              </ModalHeader>

              <ModalBody>
                <TeamInfoSection>
                  <TeamInfoGrid>
                    <InfoItem>
                      <div className="label">Team Name</div>
                      <div className="value">{selectedTeam.team_name}</div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Canton</div>
                      <div className="value">{selectedTeam.canton}</div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Skill Level</div>
                      <div className="value">
                        <SkillLevelBadge>{selectedTeam.team_skill_level}</SkillLevelBadge>
                      </div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Division</div>
                      <div className="value">
                        <DivisionBadge>{teamDetailsData?.data?.team?.division_name || 'Not assigned'}</DivisionBadge>
                      </div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Member Count</div>
                      <div className="value">{selectedTeam.member_count}/2</div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Team Phone Number</div>
                      <div className="value">
                        {teamDetailsData?.data?.team?.phone_number || selectedTeam?.phone_number || 'Not provided'}
                      </div>
                    </InfoItem>
                    <InfoItem>
                      <div className="label">Joined</div>
                      <div className="value">
                        {selectedTeam.created_at ? new Date(selectedTeam.created_at).toLocaleDateString() : 'N/A'}
                      </div>
                    </InfoItem>
                  </TeamInfoGrid>
                </TeamInfoSection>

                <MembersSection>
                  <MembersTitle>Team Members</MembersTitle>
                  {teamDetailsLoading ? (
                    <LoadingContainer>Loading team members...</LoadingContainer>
                  ) : teamDetailsData?.data?.members ? (
                    <AdminTeamMembersGrid>
                      {teamDetailsData.data.members.map((member) => (
                        <AdminTeamMemberProfileCard
                          key={member.id}
                          member={member}
                          teamId={selectedTeam?.id}
                          seasonId={seasonId || undefined}
                          onPaymentUpdated={() =>
                            queryClient.invalidateQueries(['teamDetails', selectedTeam?.id])
                          }
                        />
                      ))}
                    </AdminTeamMembersGrid>
                  ) : (
                    <EmptyContainer>
                      No team members found
                    </EmptyContainer>
                  )}
                </MembersSection>
              </ModalBody>
            </ModalContent>
          </ModalOverlay>
        )}
      </Content>
    </DivisionDetailContainer>
  );
};

export default AdminDivisionDetail;
